import { HttpClient } from '@angular/common/http';
import {
  Component,
  ElementRef,
  inject,
  type OnDestroy,
  type OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, type NavigationExtras, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import * as AuthLogin from '../../../server/routes/auth/login';
import { passwordConfig } from '../../../server/services/password-config.service';
import { loginRouteConfirmEmailErrorMessage } from '../../../server/services/utils.service';
import { AnalyticsService } from '../analytics.service';
import { AuthUserService } from '../auth-user.service';
import { HomepageLinksService } from '../homepage-links.service';
import { TitleService } from '../title.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent implements OnInit, OnDestroy {
  private authUserService = inject(AuthUserService);
  private analytics = inject(AnalyticsService);
  private titleService = inject(TitleService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private http = inject(HttpClient);
  public homepageLinks = inject(HomepageLinksService);

  form = new FormGroup({
    email: new FormControl('', {
      validators: [Validators.required, Validators.email],
      nonNullable: true,
    }),
    password: new FormControl('', {
      validators: [
        Validators.required,
        Validators.maxLength(passwordConfig.maxLength),
      ],
      nonNullable: true,
    }),
  });
  submitSuccess = false;
  waiting = false;
  formErrorMessage?: string;
  redirectUrl: string;
  @ViewChild('recaptcha', { static: true }) recaptchaElement: ElementRef;

  subscriptions = new Subscription();

  ngOnInit() {
    // The following is to ensure that when a user is redirected for the
    // purposes of logging in that they are still redirected to the correct
    // on register success.
    const paramSub = this.route.queryParams.subscribe((params) => {
      // Set the redirect after login
      this.redirectUrl = params.redirect ?? '/';
      const add = params.add; // Adding a login

      // If the user is already logged in (say user clicks back button or
      // reloads) redirect immediately
      const sub = this.authUserService.userOrgObj
        .pipe(take(1))
        .subscribe((user) => {
          if (user && !add) this.router.navigateByUrl(this.redirectUrl);
        });
      this.subscriptions.add(sub);
    });
    this.subscriptions.add(paramSub);

    this.titleService.set('Login');
  }

  submit(formData) {
    if (this.form.invalid) {
      return;
    }
    // Clear state from previous submissions
    this.formErrorMessage = undefined;
    this.waiting = true;

    const { email, password } = formData;
    const body: AuthLogin.ReqBody = { email, password };
    const sub = this.http
      .post<AuthLogin.ResBody>('/api/auth/login', body)
      .subscribe({
        next: (authResult) => {
          this.waiting = false;

          const { userOrgObj } = this.authUserService.processLogin(authResult);
          this.analytics.login('email');

          let navigationExtras: NavigationExtras | undefined;
          if (this.redirectUrl)
            navigationExtras = { queryParams: { redirect: this.redirectUrl } };
          this.router.navigate([this.redirectUrl ?? '/'], navigationExtras);
        },
        error: (errorResponse) => {
          this.waiting = false;
          if (errorResponse.status === 401) {
            if (
              errorResponse.error.message === loginRouteConfirmEmailErrorMessage
            ) {
              this.router.navigate(['/confirm-email'], {
                queryParams: { email },
              });
            } else {
              this.formErrorMessage = 'Email or password incorrect.';
            }
          } else {
            this.formErrorMessage = 'There was a problem submitting the form.';
          }
        },
      });
    this.subscriptions.add(sub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
